import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import NivelService from "../../../../../../services/LevelService";
import GradoService from "../../../../../../services/GradesService";
import UnidadService from "../../../../../../services/UnidadService";
import SACService from "../../../../../../services/SACService";
import CreateSesionService from "../../../../../../services/CreateSesionService";
import ModeracionService from "../../../../../../services/ModeracionService";
import CompetenciaService from "../../../../../../services/CompetenciaService";

import {
  createHash,
  LS_SAC_HASH,
  LS_SAC_TEMAS,
} from "./sessionSequenceStep.helpers";

// ============================================================
// UTILIDADES
// ============================================================

const normalizarTexto = (valor) => {
  if (
    valor === null ||
    valor === undefined
  ) {
    return "";
  }

  return String(valor).trim();
};

const normalizarClaveCurricular = (valor) =>
  normalizarTexto(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const tieneTemaYDescripcion = (fila) => {
  const tema = normalizarTexto(
    fila?.tema
  );

  const descripcion = normalizarTexto(
    fila?.descripcion
  );

  return Boolean(tema) && Boolean(descripcion);
};

const normalizarFilaConContenido = (
  fila,
  index
) => ({
  /*
   * Se conservan todas las propiedades recibidas:
   *
   * fileId
   * archivoDocumento
   * origenDocumento
   * datos curriculares
   * datos de unidad
   */
  ...fila,

  tema:
    normalizarTexto(fila?.tema),

  descripcion:
    normalizarTexto(
      fila?.descripcion
    ),

  nroSesion:
    fila?.nroSesion ?? index + 1,
});

// ============================================================
// HOOK
// ============================================================

const useSessionSequenceStep = ({
  data,
  onBack,
  onNext,
  setData,
}) => {
  const initialLoadRef = useRef(false);

  const [
    loadingData,
    setLoadingData,
  ] = useState(true);

  const [
    loadingGeneral,
    setLoadingGeneral,
  ] = useState(false);

  const [
    loadingGuardar,
    setLoadingGuardar,
  ] = useState(false);

  const [
    loadingContinue,
    setLoadingContinue,
  ] = useState(false);

  const [
    hayCambios,
    setHayCambios,
  ] = useState(false);

  const [
    temasGenerados,
    setTemasGenerados,
  ] = useState(false);

  const [
    openDescripcion,
    setOpenDescripcion,
  ] = useState(false);

  const [
    descripcionActiva,
    setDescripcionActiva,
  ] = useState(null);

  const [
    sesionARegenerar,
    setSesionARegenerar,
  ] = useState(null);

  const [
    regenerandoId,
    setRegenerandoId,
  ] = useState(null);

  const [
    openConfirmRegenerarTodo,
    setOpenConfirmRegenerarTodo,
  ] = useState(false);

  const [
    openConfirmSalir,
    setOpenConfirmSalir,
  ] = useState(false);

  const [
    openEdit,
    setOpenEdit,
  ] = useState(false);

  const [
    filaEditando,
    setFilaEditando,
  ] = useState(null);

  const [
    competenciasDisponibles,
    setCompetenciasDisponibles,
  ] = useState([]);

  const [
    loadingCompetencias,
    setLoadingCompetencias,
  ] = useState(false);

  const [
    alerta,
    setAlerta,
  ] = useState({
    open: false,
    mensaje: "",
    tipo: "success",
  });

  // ============================================================
  // MENSAJES
  // ============================================================

  const showSnack = useCallback(
    (
      mensaje,
      tipo = "error"
    ) => {
      setAlerta({
        open: true,
        mensaje,
        tipo,
      });
    },
    []
  );

  const nivelCompetencias = data?.[0]?.nivel;
  const areaCompetencias = data?.[0]?.area;

  useEffect(() => {
    if (!nivelCompetencias || !areaCompetencias) {
      setCompetenciasDisponibles([]);
      return;
    }

    let activo = true;
    setLoadingCompetencias(true);

    CompetenciaService
      .obtenerCompetencias(
        nivelCompetencias,
        areaCompetencias
      )
      .then((competencias) => {
        if (activo) {
          setCompetenciasDisponibles(
            Array.isArray(competencias)
              ? competencias
              : []
          );
        }
      })
      .catch((error) => {
        console.error(
          "No se pudieron cargar las competencias:",
          error
        );
        if (activo) {
          setCompetenciasDisponibles([]);
        }
      })
      .finally(() => {
        if (activo) {
          setLoadingCompetencias(false);
        }
      });

    return () => {
      activo = false;
    };
  }, [nivelCompetencias, areaCompetencias]);

  /*
   * Confirma en la segunda vista que la competencia propuesta
   * por el analizador documental pertenece al catalogo cargado.
   * Se selecciona por ID y, como respaldo, por nombre exacto
   * normalizado. Una propuesta que no coincide no se muestra
   * como seleccion valida: la fila queda sin competencia.
   */
  useEffect(() => {
    if (
      loadingCompetencias ||
      !Array.isArray(data) ||
      data.length === 0 ||
      competenciasDisponibles.length === 0
    ) {
      return;
    }

    let huboCambios = false;

    const filasValidadas = data.map(
      (fila) => {
        if (
          fila?.competenciaOrigen !==
          "IA_DOCUMENTO"
        ) {
          return fila;
        }

        const competenciaDetectada =
          competenciasDisponibles.find(
            (item) =>
              Number(item.id) ===
              Number(fila.competencia)
          ) ??
          competenciasDisponibles.find(
            (item) =>
              normalizarClaveCurricular(
                item.nombre
              ) ===
              normalizarClaveCurricular(
                fila.competenciaNombre
              )
          );

        if (!competenciaDetectada) {
          huboCambios = true;

          return {
            ...fila,
            competencia: null,
            competenciaNombre: "",
            competenciaOrigen: null,
            capacidades: [],
            desempenos: [],
          };
        }

        const competenciaId = Number(
          competenciaDetectada.id
        );
        const competenciaNombre =
          normalizarTexto(
            competenciaDetectada.nombre
          );

        if (
          Number(fila.competencia) ===
            competenciaId &&
          normalizarTexto(
            fila.competenciaNombre
          ) === competenciaNombre
        ) {
          return fila;
        }

        huboCambios = true;

        return {
          ...fila,
          competencia: competenciaId,
          competenciaNombre,
        };
      }
    );

    if (huboCambios) {
      setData(filasValidadas);
    }
  }, [
    competenciasDisponibles,
    data,
    loadingCompetencias,
    setData,
  ]);

  const getModeracionMessage =
    useCallback((err) => {
      const detail =
        err?.response?.data?.detail;

      return (
        detail?.message ||
        "Contenido no permitido " +
          "(malas palabras o +18)."
      );
    }, []);

  // ============================================================
  // MODERACIÓN
  // ============================================================

  const validarModeracion =
    useCallback(
      async (
        {
          tema,
          descripcion,
          contexto,
        },
        tipo = "error"
      ) => {
        try {
          await ModeracionService
            .validarContenido({
              tema: tema || "",
              objetivo:
                descripcion || "",
              sub_area_tematica:
                contexto || "",
            });

          return true;
        } catch (err) {
          showSnack(
            getModeracionMessage(err),
            tipo
          );

          return false;
        }
      },
      [
        getModeracionMessage,
        showSnack,
      ]
    );

  const validarFilas = useCallback(
    async (
      filas,
      tipo = "warning"
    ) => {
      for (const fila of filas) {
        const ok =
          await validarModeracion(
            {
              tema:
                fila.tema,

              descripcion:
                fila.descripcion,

              contexto:
                fila.areaNombre ||
                fila.area ||
                "",
            },
            tipo
          );

        if (!ok) {
          return false;
        }
      }

      return true;
    },
    [validarModeracion]
  );

  // ============================================================
  // DIÁLOGOS Y NAVEGACIÓN
  // ============================================================

  const abrirDetalleDescripcion = (
    row
  ) => {
    setDescripcionActiva(row);
    setOpenDescripcion(true);
  };

  const handleBackClick = () => {
    if (
      loadingGeneral ||
      regenerandoId !== null
    ) {
      return;
    }

    if (hayCambios) {
      setOpenConfirmSalir(true);
      return;
    }

    onBack();
  };

  useEffect(() => {
    const handleBeforeUnload = (
      event
    ) => {
      if (!hayCambios) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener(
      "beforeunload",
      handleBeforeUnload
    );

    return () =>
      window.removeEventListener(
        "beforeunload",
        handleBeforeUnload
      );
  }, [hayCambios]);

  // ============================================================
  // GENERAR TEMAS FALTANTES
  // ============================================================

  const generarTemas = useCallback(
    async (
      baseData = data,
      {
        forzarRegeneracion = false,
      } = {}
    ) => {
      if (
        !Array.isArray(baseData) ||
        baseData.length === 0
      ) {
        return false;
      }

      /*
       * En la carga automática solo se generan las
       * filas que no tienen tema y descripción.
       *
       * En la regeneración manual de todas las filas,
       * forzarRegeneracion será true.
       */
      const filasPendientes =
        forzarRegeneracion
          ? baseData
          : baseData.filter(
              (fila) =>
                !tieneTemaYDescripcion(
                  fila
                )
            );

      /*
       * Si todas las filas ya traen título y propósito
       * desde la vista anterior, no se llama a la IA.
       */
      if (
        filasPendientes.length === 0
      ) {
        const actualizado =
          baseData.map(
            normalizarFilaConContenido
          );

        const moderacionValida =
          await validarFilas(
            actualizado,
            "warning"
          );

        if (!moderacionValida) {
          return false;
        }

        setData(actualizado);

        localStorage.setItem(
          LS_SAC_TEMAS,
          JSON.stringify(actualizado)
        );

        setTemasGenerados(true);
        setHayCambios(false);

        return true;
      }

      setLoadingGeneral(true);

      try {
        const filaReferencia =
          filasPendientes[0];

        const payload = {
          n:
            filasPendientes.length,

          unidad:
            filaReferencia
              .unidadNombre,

          situacion_signif:
            filaReferencia
              .situacion,

          nivel_id:
            filaReferencia.nivel,

          grado_id:
            filaReferencia.grado,

          area_id:
            filaReferencia.area,

          competencia_id:
            filaReferencia.competencia,

          capacidades_ids:
            Array.isArray(
              filaReferencia.capacidades
            )
              ? filaReferencia.capacidades
              : [],

          desempenos_ids:
            Array.isArray(
              filaReferencia.desempenos
            )
              ? filaReferencia.desempenos
              : [],

          forzar_competencia: false,
        };

        const resp =
          await SACService
            .generarTemas(payload);

        const resultados =
          Array.isArray(
            resp?.resultado
          )
            ? resp.resultado
            : [];

        let indiceResultado = 0;

        const actualizado =
          baseData.map(
            (fila, index) => {
              /*
               * Las propiedades adicionales se conservan
               * mediante ...fila, incluido fileId.
               */
              if (
                !forzarRegeneracion &&
                tieneTemaYDescripcion(
                  fila
                )
              ) {
                return {
                  ...fila,

                  tema:
                    normalizarTexto(
                      fila.tema
                    ),

                  descripcion:
                    normalizarTexto(
                      fila.descripcion
                    ),

                  nroSesion:
                    index + 1,
                };
              }

              const resultado =
                resultados[
                  indiceResultado
                ];

              indiceResultado += 1;

              return {
                ...fila,

                tema:
                  normalizarTexto(
                    resultado?.tema
                  ) ||
                  normalizarTexto(
                    fila.tema
                  ) ||
                  `Tema ${index + 1}`,

                descripcion:
                  normalizarTexto(
                    resultado
                      ?.descripcion
                  ) ||
                  normalizarTexto(
                    fila.descripcion
                  ) ||
                  `Descripción ${index + 1}`,

                competencia:
                  Number(
                    resultado?.competencia_id
                  ) || fila.competencia,

                competenciaNombre:
                  normalizarTexto(
                    resultado?.competencia_nombre
                  ) || fila.competenciaNombre || "",

                capacidades:
                  Array.isArray(
                    resultado?.capacidades_ids
                  )
                    ? resultado.capacidades_ids
                    : fila.capacidades,

                desempenos:
                  Array.isArray(
                    resultado?.desempenos_ids
                  )
                    ? resultado.desempenos_ids
                    : fila.desempenos,

                competenciaOrigen:
                  resultado?.competencia_id
                    ? "IA"
                    : fila.competenciaOrigen,

                nroSesion:
                  index + 1,
              };
            }
          );

        const moderacionValida =
          await validarFilas(
            actualizado,
            "warning"
          );

        if (!moderacionValida) {
          return false;
        }

        setData(actualizado);

        localStorage.setItem(
          LS_SAC_TEMAS,
          JSON.stringify(actualizado)
        );

        setTemasGenerados(false);
        setHayCambios(true);

        setAlerta({
          open: true,

          mensaje:
            forzarRegeneracion
              ? (
                  "Temas regenerados " +
                  "correctamente."
                )
              : (
                  "Temas generados " +
                  "correctamente."
                ),

          tipo: "success",
        });

        return true;
      } catch (error) {
        console.error(
          "Error generando temas:",
          error
        );

        setAlerta({
          open: true,
          mensaje:
            "Error generando temas",
          tipo: "error",
        });

        return false;
      } finally {
        setLoadingGeneral(false);
        setRegenerandoId(null);
      }
    },
    [
      data,
      setData,
      validarFilas,
    ]
  );

  // ============================================================
  // REGENERAR UNA FILA
  // ============================================================

  const generarTemaIndividual =
    async (
      fila,
      {
        competenciaOrigen =
          fila.competenciaOrigen || "USUARIO",
      } = {}
    ) => {
      const index = data.findIndex(
        (item) =>
          (
            item.id ||
            item.nroSesion
          ) ===
          (
            fila.id ||
            fila.nroSesion
          )
      );

      if (index === -1) {
        setRegenerandoId(null);
        return;
      }

      try {
        const payload = {
          n: 1,

          unidad:
            fila.unidadNombre,

          situacion_signif:
            fila.situacion,

          nivel_id:
            fila.nivel,

          grado_id:
            fila.grado,

          area_id:
            fila.area,

          competencia_id:
            fila.competencia,

          capacidades_ids:
            Array.isArray(
              fila.capacidades
            )
              ? fila.capacidades
              : [],

          desempenos_ids:
            Array.isArray(
              fila.desempenos
            )
              ? fila.desempenos
              : [],

          forzar_competencia: true,
        };

        const resp =
          await SACService
            .generarTemas(payload);

        const resultado =
          resp?.resultado?.[0];

        const temaGenerado =
          normalizarTexto(
            resultado?.tema
          );

        const descripcionGenerada =
          normalizarTexto(
            resultado?.descripcion
          );

        const filaRegenerada = {
          /*
           * Conserva fileId y los metadatos del documento.
           */
          ...data[index],

          tema:
            temaGenerado ||
            data[index].tema,

          descripcion:
            descripcionGenerada ||
            data[index].descripcion,

          competencia:
            Number(
              resultado?.competencia_id
            ) || fila.competencia,

          competenciaNombre:
            normalizarTexto(
              resultado?.competencia_nombre
            ) || fila.competenciaNombre || "",

          capacidades:
            Array.isArray(
              resultado?.capacidades_ids
            )
              ? resultado.capacidades_ids
              : fila.capacidades,

          desempenos:
            Array.isArray(
              resultado?.desempenos_ids
            )
              ? resultado.desempenos_ids
              : fila.desempenos,

          competenciaOrigen,
        };

        const moderacionValida =
          await validarModeracion(
            {
              tema:
                filaRegenerada.tema,

              descripcion:
                filaRegenerada
                  .descripcion,

              contexto:
                filaRegenerada
                  .areaNombre ||
                filaRegenerada.area ||
                "",
            },
            "warning"
          );

        if (!moderacionValida) {
          return;
        }

        setData((prev) => {
          const copia = [...prev];

          copia[index] = {
            ...copia[index],
            ...filaRegenerada,
          };

          localStorage.setItem(
            LS_SAC_TEMAS,
            JSON.stringify(copia)
          );

          return copia;
        });

        setHayCambios(true);
        setTemasGenerados(false);
      } catch (error) {
        console.error(
          "Error regenerando la sesión:",
          error
        );

        showSnack(
          "Error regenerando la sesión",
          "error"
        );
      } finally {
        setRegenerandoId(null);
      }
    };

  const cambiarCompetencia = async (
    fila,
    competenciaId
  ) => {
    const nuevaCompetenciaId = Number(
      competenciaId
    );

    if (
      !nuevaCompetenciaId ||
      nuevaCompetenciaId ===
        Number(fila.competencia)
    ) {
      return;
    }

    const competenciaSeleccionada =
      competenciasDisponibles.find(
        (item) =>
          Number(item.id) ===
          nuevaCompetenciaId
      );
    const id = fila.id || fila.nroSesion;

    setRegenerandoId(id);

    try {
      /*
       * Cambiar la competencia no debe regenerar el contenido de la
       * sesión. Solo se actualiza su selección curricular con los datos
       * que corresponden a la nueva competencia.
       */
      const [
        capacidadesDisponibles,
        desempenosDisponibles,
      ] = await Promise.all([
        CompetenciaService.obtenerCapacidades(
          fila.nivel,
          fila.area,
          nuevaCompetenciaId
        ),
        CompetenciaService.obtenerDesempeños(
          fila.nivel,
          fila.area,
          nuevaCompetenciaId,
          fila.grado
        ),
      ]);

      const capacidadesIds = Array.isArray(
        capacidadesDisponibles
      )
        ? capacidadesDisponibles.map(
            (item) => item.id
          )
        : [];

      const desempenosIds = Array.isArray(
        desempenosDisponibles
      )
        ? desempenosDisponibles.map(
            (item) => item.id
          )
        : [];

      setData((prev) => {
        const actualizado = prev.map(
          (item) =>
            (
              item.id || item.nroSesion
            ) === id
              ? {
                  ...item,
                  competencia:
                    nuevaCompetenciaId,
                  competenciaNombre:
                    competenciaSeleccionada
                      ?.nombre || "",
                  capacidades:
                    capacidadesIds,
                  desempenos:
                    desempenosIds,
                  competenciaOrigen:
                    "USUARIO",
                }
              : item
        );

        localStorage.setItem(
          LS_SAC_TEMAS,
          JSON.stringify(actualizado)
        );

        return actualizado;
      });

      setHayCambios(true);
      setTemasGenerados(false);

      showSnack(
        "Competencia actualizada sin modificar el título ni la descripción.",
        "success"
      );
    } catch (error) {
      console.error(
        "Error actualizando la competencia de la sesión:",
        error
      );

      showSnack(
        "No se pudo actualizar la competencia.",
        "error"
      );
    } finally {
      setRegenerandoId(null);
    }
  };

  // ============================================================
  // CARGA INICIAL
  // ============================================================

  useEffect(() => {
    if (
      !Array.isArray(data) ||
      data.length === 0
    ) {
      setLoadingData(false);
      return;
    }

    if (initialLoadRef.current) {
      return;
    }

    initialLoadRef.current = true;

    const cargar = async () => {
      setLoadingData(true);

      try {
        /*
         * MODIFICACIÓN:
         *
         * fileId forma parte del hash para evitar que
         * se reutilicen temas guardados pertenecientes
         * a otro documento con los mismos datos
         * curriculares.
         */
        const hashActual =
          createHash({
            nivel:
              data[0].nivel,

            grado:
              data[0].grado,

            area:
              data[0].area,

            unidadId:
              data[0].unidadId,

            capacidades:
              data[0].capacidades,

            desempenos:
              data[0].desempenos,

            fileId:
              data[0].fileId ??
              null,
          });

        const hashPrevio =
          localStorage.getItem(
            LS_SAC_HASH
          );

        const hayCambiosHash =
          hashPrevio !== hashActual;

        localStorage.setItem(
          LS_SAC_HASH,
          hashActual
        );

        const [
          nivelesRes,
          unidadesRes,
        ] = await Promise.all([
          NivelService.listarNiveles(),

          UnidadService
            .obtenerUnidades({
              idInstitucionEdu:
                data[0].institucionId,
            }),
        ]);

        const niveles =
          Array.isArray(
            nivelesRes?.data
          )
            ? nivelesRes.data
            : [];

        const unidades =
          Array.isArray(
            unidadesRes?.items
          )
            ? unidadesRes.items
            : [];

        const reconstruido = [];

        for (const fila of data) {
          const [
            gradosRes,
            areasRes,
          ] = await Promise.all([
            NivelService
              .listarGradosPorNivel(
                fila.nivel
              ),

            GradoService
              .listarAreasPorNivel(
                fila.nivel
              ),
          ]);

          const grados =
            Array.isArray(
              gradosRes?.data
            )
              ? gradosRes.data
              : [];

          const areas =
            Array.isArray(
              areasRes?.data
            )
              ? areasRes.data
              : [];

          const nivelObj =
            niveles.find(
              (item) =>
                Number(item.id) ===
                Number(fila.nivel)
            );

          const gradoObj =
            grados.find(
              (item) =>
                Number(item.id) ===
                Number(fila.grado)
            );

          const areaObj =
            areas.find(
              (item) =>
                Number(item.id) ===
                Number(fila.area)
            );

          const unidadObj =
            unidades.find(
              (item) =>
                Number(item.id) ===
                Number(fila.unidadId)
            );

          reconstruido.push({
            /*
             * Conserva:
             *
             * fileId
             * archivoDocumento
             * origenDocumento
             * tituloSesion
             * propositoSesion
             */
            ...fila,

            tema:
              normalizarTexto(
                fila.tema
              ),

            descripcion:
              normalizarTexto(
                fila.descripcion
              ),

            nivelNombre:
              nivelObj?.nombre,

            gradoNombre:
              gradoObj
                ?.descripcion ??
              gradoObj?.nombre ??
              gradoObj?.numero,

            areaNombre:
              areaObj?.nombre,

            unidadNombre:
              unidadObj
                ?.nombreUnidad ??
              fila.unidadNombre ??
              fila.unidad,

            nroUnidad:
              unidadObj?.nroUnidad ??
              fila.nroUnidad,

            situacion:
              unidadObj
                ?.situacionSignificativa ??
              fila.situacion,
          });
        }

        const temasGuardadosRaw =
          localStorage.getItem(
            LS_SAC_TEMAS
          );

        let temasGuardados = [];

        if (
          !hayCambiosHash &&
          temasGuardadosRaw
        ) {
          try {
            const contenidoGuardado =
              JSON.parse(
                temasGuardadosRaw
              );

            temasGuardados =
              Array.isArray(
                contenidoGuardado
              )
                ? contenidoGuardado
                : [];
          } catch (error) {
            console.error(
              "No se pudieron leer los " +
                "temas guardados:",
              error
            );

            localStorage.removeItem(
              LS_SAC_TEMAS
            );
          }
        }

        /*
         * Prioridad:
         *
         * 1. Tema y descripción recibidos desde
         *    la primera vista.
         *
         * 2. Tema y descripción guardados.
         *
         * 3. Generación mediante IA.
         *
         * Los metadatos del documento siempre provienen
         * de la fila actual, no de la fila guardada.
         */
        const filasPreparadas =
          reconstruido.map(
            (fila, index) => {
              const temaEntrante =
                normalizarTexto(
                  fila.tema
                );

              const descripcionEntrante =
                normalizarTexto(
                  fila.descripcion
                );

              const tieneContenidoEntrante =
                Boolean(
                  temaEntrante
                ) &&
                Boolean(
                  descripcionEntrante
                );

              const filaGuardada =
                temasGuardados[index];

              const temaGuardado =
                normalizarTexto(
                  filaGuardada?.tema
                );

              const descripcionGuardada =
                normalizarTexto(
                  filaGuardada
                    ?.descripcion
                );

              const usarDatosEntrantes =
                tieneContenidoEntrante;

              return {
                /*
                 * Se utiliza la fila reconstruida como
                 * base para conservar el fileId actual.
                 */
                ...fila,

                tema:
                  tieneContenidoEntrante
                    ? temaEntrante
                    : temaGuardado,

                descripcion:
                  usarDatosEntrantes
                    ? descripcionEntrante
                    : descripcionGuardada,

                competencia:
                  usarDatosEntrantes
                    ? fila.competencia
                    : (
                        filaGuardada
                          ?.competencia ??
                        fila.competencia
                      ),

                competenciaNombre:
                  usarDatosEntrantes
                    ? fila.competenciaNombre
                    : (
                        filaGuardada
                          ?.competenciaNombre ??
                        fila.competenciaNombre
                      ),

                capacidades:
                  usarDatosEntrantes
                    ? fila.capacidades
                    : (
                        filaGuardada
                          ?.capacidades ??
                        fila.capacidades
                      ),

                desempenos:
                  usarDatosEntrantes
                    ? fila.desempenos
                    : (
                        filaGuardada
                          ?.desempenos ??
                        fila.desempenos
                      ),

                competenciaOrigen:
                  usarDatosEntrantes
                    ? fila.competenciaOrigen
                    : (
                        filaGuardada
                          ?.competenciaOrigen ??
                        fila.competenciaOrigen
                      ),

                nroSesion:
                  tieneContenidoEntrante
                    ? (
                        fila.nroSesion ??
                        index + 1
                      )
                    : (
                        filaGuardada
                          ?.nroSesion ??
                        fila.nroSesion ??
                        index + 1
                      ),
              };
            }
          );

        const todasTienenContenido =
          filasPreparadas.every(
            tieneTemaYDescripcion
          );

        if (todasTienenContenido) {
          const filasNormalizadas =
            filasPreparadas.map(
              normalizarFilaConContenido
            );

          const moderacionValida =
            await validarFilas(
              filasNormalizadas,
              "warning"
            );

          if (!moderacionValida) {
            return;
          }

          setData(
            filasNormalizadas
          );

          localStorage.setItem(
            LS_SAC_TEMAS,
            JSON.stringify(
              filasNormalizadas
            )
          );

          setTemasGenerados(true);
          setHayCambios(false);
        } else {
          await generarTemas(
            filasPreparadas
          );
        }
      } catch (error) {
        console.error(
          "Error cargando la secuencia " +
            "de sesiones:",
          error
        );

        showSnack(
          "No se pudo cargar la " +
            "secuencia de sesiones.",
          "error"
        );
      } finally {
        setLoadingData(false);
      }
    };

    cargar();
  }, [
    data,
    generarTemas,
    setData,
    showSnack,
    validarFilas,
  ]);

  // ============================================================
  // ORDENAR FILAS
  // ============================================================

  const moverFila = (
    index,
    direction
  ) => {
    setData((prev) => {
      const copia = [...prev];

      if (
        direction === "up" &&
        index > 0
      ) {
        [
          copia[index - 1],
          copia[index],
        ] = [
          copia[index],
          copia[index - 1],
        ];
      }

      if (
        direction === "down" &&
        index <
          copia.length - 1
      ) {
        [
          copia[index + 1],
          copia[index],
        ] = [
          copia[index],
          copia[index + 1],
        ];
      }

      /*
       * Las filas mantienen sus propiedades completas.
       */
      const renumerado =
        copia.map(
          (fila, rowIndex) => ({
            ...fila,

            nroSesion:
              rowIndex + 1,
          })
        );

      localStorage.setItem(
        LS_SAC_TEMAS,
        JSON.stringify(renumerado)
      );

      setHayCambios(true);
      setTemasGenerados(false);

      return renumerado;
    });
  };

  // ============================================================
  // EDITAR FILA
  // ============================================================

  const abrirEditar = (fila) => {
    setFilaEditando({
      ...fila,
    });

    setOpenEdit(true);
  };

  const guardarEdicion = async () => {
    if (!filaEditando) {
      return;
    }

    const ok =
      await validarModeracion(
        {
          tema:
            filaEditando.tema,

          descripcion:
            filaEditando
              .descripcion,

          contexto:
            filaEditando
              .areaNombre ||
            filaEditando.area ||
            "",
        },
        "error"
      );

    if (!ok) {
      return;
    }

    const actualizado =
      data.map((fila) =>
        fila.nroSesion ===
        filaEditando.nroSesion
          ? {
              /*
               * filaEditando conserva fileId porque fue
               * creada originalmente mediante ...fila.
               */
              ...filaEditando,

              tema:
                normalizarTexto(
                  filaEditando.tema
                ),

              descripcion:
                normalizarTexto(
                  filaEditando
                    .descripcion
                ),
            }
          : fila
      );

    setData(actualizado);

    localStorage.setItem(
      LS_SAC_TEMAS,
      JSON.stringify(actualizado)
    );

    setHayCambios(true);
    setTemasGenerados(false);
    setOpenEdit(false);

    showSnack(
      "Cambios guardados correctamente.",
      "success"
    );
  };

  // ============================================================
  // GUARDAR
  // ============================================================

  const handleGuardar = async () => {
    setLoadingGuardar(true);

    try {
      /*
       * Se guarda la fila completa, incluido fileId.
       */
      localStorage.setItem(
        LS_SAC_TEMAS,
        JSON.stringify(data)
      );

      setHayCambios(false);
      setTemasGenerados(true);

      setAlerta({
        open: true,
        mensaje:
          "Temas guardados correctamente.",
        tipo: "success",
      });
    } finally {
      setLoadingGuardar(false);
    }
  };

  // ============================================================
  // CONTINUAR
  // ============================================================

  // const handleContinuar = async () => {
  //   if (loadingContinue) {
  //     return;
  //   }

  //   setLoadingContinue(true);

  //   try {
  //     const dataConId = [];

  //     for (const fila of data) {
  //       const ok =
  //         await validarModeracion(
  //           {
  //             tema:
  //               fila.tema,

  //             descripcion:
  //               fila.descripcion,

  //             contexto:
  //               fila.areaNombre ||
  //               fila.area ||
  //               "",
  //           },
  //           "error"
  //         );

  //       if (!ok) {
  //         throw new Error(
  //           "Bloqueado por moderación"
  //         );
  //       }

  //       if (!fila.id_PSQL) {
  //         const resp =
  //           await CreateSesionService
  //             .crearSesionSAC({
  //               nivel_id:
  //                 fila.nivel,

  //               grado_id:
  //                 fila.grado,

  //               area_tematica_id:
  //                 fila.area,

  //               tema:
  //                 fila.tema,

  //               objetivo:
  //                 fila.descripcion,

  //               file_id:fila.fileId ?? fila.file_id?? null,

  //               sac: {
  //                 nro_sesion:
  //                   fila.nroSesion,

  //                 unidad_anual_id:
  //                   fila.unidadId,
  //               },
  //             });

  //         /*
  //          * Se agrega id_PSQL sin eliminar:
  //          *
  //          * fileId
  //          * archivoDocumento
  //          * origenDocumento
  //          */
  //         dataConId.push({
  //           ...fila,

  //           id_PSQL:
  //             resp.id,
  //         });
  //       } else {
  //         dataConId.push(fila);
  //       }
  //     }

  //     setData(dataConId);

  //     /*
  //      * onNext recibe las filas completas.
  //      * fileId llegará así a la tercera vista.
  //      */
  //     onNext(dataConId);
  //   } catch (error) {
  //     console.error(
  //       "Error continuando con las sesiones:",
  //       error
  //     );

  //     showSnack(
  //       "Error procesando la sesión",
  //       "error"
  //     );
  //   } finally {
  //     setLoadingContinue(false);
  //   }
  // };

  const handleContinuar = async () => {
    if (loadingContinue) {
      return;
    }

    setLoadingContinue(true);

    try {
      const dataConId = [];

      for (const fila of data) {
        if (!fila.competencia) {
          showSnack(
            `La sesión ${fila.nroSesion} no tiene una competencia seleccionada.`,
            "warning"
          );
          throw new Error(
            "Sesión sin competencia seleccionada"
          );
        }

        const ok =
          await validarModeracion(
            {
              tema:
                fila.tema,

              descripcion:
                fila.descripcion,

              contexto:
                fila.areaNombre ||
                fila.area ||
                "",
            },
            "error"
          );

        if (!ok) {
          throw new Error(
            "Bloqueado por moderación"
          );
        }

        const fileIdActual =
          fila.fileId ??
          fila.file_id ??
          null;

        /*
        * Este log permite confirmar que la fila
        * todavía conserva el file_id.
        */
        console.log(
          "FILE ID ANTES DE crearSAC:",
          fileIdActual
        );

        if (!fila.id_PSQL) {
          const payloadCrearSAC = {
            nivel_id:
              fila.nivel,

            grado_id:
              fila.grado,

            area_tematica_id:
              fila.area,

            tema:
              fila.tema,

            objetivo:
              fila.descripcion,

            file_id:
              fileIdActual,

            sac: {
              nro_sesion:
                fila.nroSesion,

              unidad_anual_id:
                fila.unidadId,

              competencia_id:
                fila.competencia,

              capacidades_ids:
                Array.isArray(fila.capacidades)
                  ? fila.capacidades
                  : [],

              desempenos_ids:
                Array.isArray(fila.desempenos)
                  ? fila.desempenos
                  : [],
            },
          };

          /*
          * Debe aparecer file_id: "file-..."
          * en la consola y en Network → Payload.
          */
          console.log(
            "PAYLOAD crearSAC:",
            payloadCrearSAC
          );

          const resp =
            await CreateSesionService
              .crearSesionSAC(
                payloadCrearSAC
              );

          console.log(
            "RESPUESTA crearSAC:",
            resp
          );

          /*
          * Conserva todas las propiedades locales.
          *
          * No depende de que el backend devuelva file_id,
          * porque la fila ya lo tenía antes de crear la SAC.
          */
          dataConId.push({
            ...fila,

            id_PSQL:
              resp?.id ??
              fila.id_PSQL ??
              null,

            fileId:
              fileIdActual ??
              resp?.file_id ??
              resp?.fileId ??
              null,

            file_id:
              fileIdActual ??
              resp?.file_id ??
              resp?.fileId ??
              null,

            archivoDocumento:
              fila.archivoDocumento ??
              null,

            origenDocumento:
              Boolean(
                fila.origenDocumento ||
                fileIdActual
              ),
          });
        } else {
          /*
          * También se normaliza cuando la sesión
          * ya existe en PostgreSQL.
          */
          dataConId.push({
            ...fila,

            fileId:
              fileIdActual,

            file_id:
              fileIdActual,

            origenDocumento:
              Boolean(
                fila.origenDocumento ||
                fileIdActual
              ),
          });
        }
      }

      console.log(
        "FILAS ENVIADAS A LA VISTA 3:",
        dataConId
      );

      setData(dataConId);

      /*
      * Se guardan también las filas completas para evitar
      * perder fileId al recargar o volver entre vistas.
      */
      localStorage.setItem(
        LS_SAC_TEMAS,
        JSON.stringify(dataConId)
      );

      onNext(dataConId);
    } catch (error) {
      console.error(
        "Error continuando con las sesiones:",
        error
      );

      if (
        error?.message !==
        "Sesión sin competencia seleccionada"
      ) {
        showSnack(
          "Error procesando la sesión",
          "error"
        );
      }
    } finally {
      setLoadingContinue(false);
    }
  };

  // ============================================================
  // CONFIRMAR REGENERACIÓN INDIVIDUAL
  // ============================================================

  const handleConfirmRegenerateRow =
    () => {
      if (!sesionARegenerar) {
        return;
      }

      const id =
        sesionARegenerar.id ||
        sesionARegenerar
          .nroSesion;

      setRegenerandoId(id);

      generarTemaIndividual(
        sesionARegenerar
      );

      setSesionARegenerar(null);
    };

  // ============================================================
  // RESPUESTA DEL HOOK
  // ============================================================

  return {
    alerta,
    setAlerta,

    data,
    competenciasDisponibles,
    loadingCompetencias,

    descripcionActiva,
    filaEditando,

    hayCambios,

    loadingContinue,
    loadingData,
    loadingGeneral,
    loadingGuardar,

    openConfirmRegenerarTodo,
    setOpenConfirmRegenerarTodo,

    openConfirmSalir,
    setOpenConfirmSalir,

    openDescripcion,
    setOpenDescripcion,

    openEdit,
    setOpenEdit,

    regenerandoId,

    sesionARegenerar,
    setSesionARegenerar,

    temasGenerados,

    setFilaEditando,

    abrirDetalleDescripcion,
    abrirEditar,
    cambiarCompetencia,
    guardarEdicion,

    handleBackClick,
    handleContinuar,
    handleGuardar,

    moverFila,

    onBack,

    onConfirmRegenerateAll: () => {
      setOpenConfirmRegenerarTodo(
        false
      );

      generarTemas(
        data,
        {
          forzarRegeneracion: true,
        }
      );
    },

    onConfirmRegenerateRow:
      handleConfirmRegenerateRow,
  };
};

export default useSessionSequenceStep;
